import React, { useState, useEffect, useRef } from "react";
import {
	Box,
	Paper,
	Typography,
	Grid,
	Card,
	CardContent,
	CardMedia,
	CardActionArea,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	LinearProgress,
	Chip,
	IconButton,
	Stack,
	Alert,
} from "@mui/material";
import {
	PlayCircle as PlayIcon,
	CheckCircle as CheckCircleIcon,
	Clock as ClockIcon,
	XCircle as CloseIcon,
} from "lucide-react";

const TeacherOnboardingVideos = ({ supabase, user }) => {
	const [videos, setVideos] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedVideo, setSelectedVideo] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [progress, setProgress] = useState({});
	const videoRef = useRef(null);
	const [progressUpdated, setProgressUpdated] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");

	// Fetch all videos and user progress
	useEffect(() => {
		const fetchVideosAndProgress = async () => {
			try {
				setLoading(true);

				// Fetch all onboarding videos
				const { data: videosData, error: videosError } = await supabase
					.from("onboarding_videos")
					.select("*")
					.order("playorder");

				if (videosError) throw videosError;

				// Fetch user progress for these videos
				const { data: progressData, error: progressError } = await supabase
					.from("teacher_onboarding_progress")
					.select("*")
					.eq("teacher_id", user.id);

				if (progressError) throw progressError;

				// Create a map of video_id to progress
				const progressMap = {};
				progressData.forEach((item) => {
					progressMap[item.video_id] = item;
				});

				// Initialize progress for videos that don't have progress entries yet
				const videosWithInitializedProgress = await Promise.all(
					videosData.map(async (video) => {
						if (!progressMap[video.id]) {
							// Create a new progress entry
							const { data, error } = await supabase
								.from("teacher_onboarding_progress")
								.insert({
									teacher_id: user.id,
									video_id: video.id,
									status: "not_watched",
									progress_percentage: 0,
								})
								.select()
								.single();

							if (error) throw error;
							progressMap[video.id] = data;
						}

						return {
							...video,
							progress: progressMap[video.id],
						};
					})
				);

				setVideos(videosWithInitializedProgress);

				// Set up progress state
				const initialProgress = {};
				videosWithInitializedProgress.forEach((video) => {
					initialProgress[video.id] = video.progress?.progress_percentage || 0;
				});
				setProgress(initialProgress);
			} catch (error) {
				console.error("Error fetching onboarding videos:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchVideosAndProgress();
	}, [supabase, user.id, progressUpdated]);

	// Handle video selection
	const handleVideoSelect = (video) => {
		setSelectedVideo(video);
		setOpenDialog(true);
		setSuccessMessage("");
	};

	// Close video dialog
	const handleCloseDialog = async () => {
		if (videoRef.current) {
			videoRef.current.pause();

			// Update progress in the database if needed
			if (selectedVideo) {
				const currentProgress = Math.round(
					(videoRef.current.currentTime / videoRef.current.duration) * 100
				);

				// Only update if progress changed significantly or video is complete
				if (
					currentProgress > (progress[selectedVideo.id] || 0) + 5 ||
					currentProgress >= 95
				) {
					const status = currentProgress >= 95 ? "completed" : "in_progress";

					await supabase
						.from("teacher_onboarding_progress")
						.update({
							progress_percentage: currentProgress,
							status: status,
							watched_at:
								status === "completed" ? new Date().toISOString() : null,
						})
						.eq("teacher_id", user.id)
						.eq("video_id", selectedVideo.id);

					// Toggle progress updated to trigger refetch
					setProgressUpdated(!progressUpdated);
				}
			}
		}

		setOpenDialog(false);
		setSelectedVideo(null);
		setSuccessMessage("");
	};

	// Track video timeupdate event
	const handleTimeUpdate = () => {
		if (videoRef.current && selectedVideo) {
			const currentProgress = Math.round(
				(videoRef.current.currentTime / videoRef.current.duration) * 100
			);

			setProgress((prev) => ({
				...prev,
				[selectedVideo.id]: currentProgress,
			}));
		}
	};

	// Handle video ended event
	const handleVideoEnded = async () => {
		if (selectedVideo) {
			await supabase
				.from("teacher_onboarding_progress")
				.update({
					progress_percentage: 100,
					status: "completed",
					watched_at: new Date().toISOString(),
				})
				.eq("teacher_id", user.id)
				.eq("video_id", selectedVideo.id);

			// Toggle progress updated to trigger refetch
			setProgressUpdated(!progressUpdated);
		}
	};

	// Store the last valid time to prevent seeking forward
	const [lastValidTime, setLastValidTime] = useState(0);

	// Update last valid time regularly during playback
	useEffect(() => {
		const interval = setInterval(() => {
			if (videoRef.current && videoRef.current.paused === false) {
				setLastValidTime(videoRef.current.currentTime);
			}
		}, 1000); // Update every second

		return () => clearInterval(interval);
	}, []);

	// Handle seeking event to prevent fast-forwarding
	const handleSeeking = () => {
		if (videoRef.current) {
			// If trying to seek forward beyond a small buffer, prevent it
			if (videoRef.current.currentTime > lastValidTime + 2) {
				videoRef.current.currentTime = lastValidTime;

				// Show a notification that fast-forwarding is disabled
				setSuccessMessage("Fast-forwarding is disabled for onboarding videos");
				setTimeout(() => setSuccessMessage(""), 3000);
			}
		}
	};

	// Calculate overall progress
	const calculateOverallProgress = () => {
		if (videos.length === 0) return 0;

		const total = videos.reduce((sum, video) => {
			return sum + (video.progress?.progress_percentage || 0);
		}, 0);

		return Math.round(total / videos.length);
	};

	// Get status chip for a video
	const getStatusChip = (video) => {
		const status = video.progress?.status || "not_watched";

		switch (status) {
			case "completed":
				return (
					<Chip
						icon={<CheckCircleIcon size={16} />}
						label="Completed"
						color="success"
						size="small"
					/>
				);
			case "in_progress":
				return (
					<Chip
						icon={<ClockIcon size={16} />}
						label="In Progress"
						color="warning"
						size="small"
					/>
				);
			default:
				return (
					<Chip
						icon={<ClockIcon size={16} />}
						label="Not Watched"
						color="default"
						size="small"
					/>
				);
		}
	};

	return (
		<Paper
			elevation={0}
			sx={{
				p: 3,
				mb: 3,
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 2,
			}}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
				}}>
				<Typography variant="h6">Onboarding Videos</Typography>
				<Box sx={{ display: "flex", alignItems: "center" }}>
					<Typography variant="body2" sx={{ mr: 1 }}>
						Overall Progress: {calculateOverallProgress()}%
					</Typography>
				</Box>
			</Box>

			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				Welcome to your teacher onboarding journey! Please watch all the
				following videos in sequence to get started with our platform. These
				videos will guide you through the essential features and help you make
				the most of our teaching tools.
			</Typography>

			<LinearProgress
				variant="determinate"
				value={calculateOverallProgress()}
				sx={{ mb: 3, height: 8, borderRadius: 4 }}
			/>

			{loading ? (
				<Box sx={{ py: 4, textAlign: "center" }}>
					<Typography>Loading onboarding videos...</Typography>
				</Box>
			) : videos.length === 0 ? (
				<Box sx={{ py: 4, textAlign: "center" }}>
					<Typography>No onboarding videos available at this time.</Typography>
				</Box>
			) : (
				<Grid container spacing={3}>
					{videos.map((video, index) => (
						<Grid item xs={12} sm={6} md={4} key={video.id}>
							<Card
								elevation={1}
								sx={{
									height: "100%",
									display: "flex",
									flexDirection: "column",
									position: "relative",
									transition: "transform 0.2s",
									"&:hover": {
										transform: "translateY(-5px)",
									},
								}}>
								<Box sx={{ position: "relative" }}>
									<CardMedia
										component="div"
										sx={{
											height: 140,
											bgcolor: "action.selected",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											position: "relative",
											backgroundImage: video.thumbnail
												? `url(${video.thumbnail})`
												: "none",
											backgroundSize: "cover",
											backgroundPosition: "center",
										}}>
										{/* {!video.thumbnail && (
											<Typography
												variant="h1"
												sx={{
													position: "absolute",
													fontSize: "72px",
													fontWeight: "bold",
													opacity: 0.2,
													color: "primary.main",
												}}>
												{index + 1}
											</Typography>
										)} */}
										<IconButton
											sx={{
												position: "absolute",
												fontSize: "48px",
											}}
											onClick={() => handleVideoSelect(video)}>
											<PlayIcon size={48} color="#fff" />
										</IconButton>
									</CardMedia>
									<LinearProgress
										variant="determinate"
										value={progress[video.id] || 0}
										sx={{ height: 4 }}
									/>
								</Box>
								<CardContent sx={{ flexGrow: 1 }}>
									<Stack
										direction="row"
										justifyContent="space-between"
										alignItems="center"
										sx={{ mb: 1 }}>
										<Typography variant="caption" color="text.secondary">
											Video {index + 1}
										</Typography>
										{getStatusChip(video)}
									</Stack>
									<Typography
										variant="subtitle1"
										gutterBottom
										component="div"
										sx={{ fontWeight: "medium" }}>
										{video.title}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{video.description}
									</Typography>
								</CardContent>
								<CardActionArea
									onClick={() => handleVideoSelect(video)}
									sx={{
										p: 1,
										display: "flex",
										justifyContent: "center",
										borderTop: "1px solid",
										borderColor: "divider",
									}}>
									<Typography variant="button" color="primary">
										{progress[video.id] > 0 && progress[video.id] < 95
											? "Continue Watching"
											: "Watch Video"}
									</Typography>
								</CardActionArea>
							</Card>
						</Grid>
					))}
				</Grid>
			)}

			{/* Video Player Dialog */}
			<Dialog
				open={openDialog}
				onClose={handleCloseDialog}
				maxWidth="md"
				fullWidth>
				{selectedVideo && (
					<>
						<DialogTitle>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
								}}>
								<Typography variant="h6">{selectedVideo.title}</Typography>
								<IconButton onClick={handleCloseDialog} edge="end">
									<CloseIcon />
								</IconButton>
							</Box>
						</DialogTitle>
						<DialogContent>
							{/* Alert for fast-forwarding */}
							{successMessage && (
								<Alert
									severity="info"
									sx={{ mb: 2 }}
									action={
										<IconButton
											aria-label="close"
											color="inherit"
											size="small"
											onClick={() => setSuccessMessage("")}>
											<CloseIcon fontSize="inherit" />
										</IconButton>
									}>
									{successMessage}
								</Alert>
							)}
							<Box sx={{ position: "relative" }}>
								<video
									ref={videoRef}
									src={selectedVideo.video_link}
									poster={selectedVideo.thumbnail || ""}
									controls
									width="100%"
									style={{ maxHeight: "70vh" }}
									onTimeUpdate={handleTimeUpdate}
									onEnded={handleVideoEnded}
									onSeeking={handleSeeking}
									autoPlay
								/>
							</Box>
							<Box sx={{ mt: 2 }}>
								<Typography variant="body1">
									{selectedVideo.description}
								</Typography>
							</Box>
						</DialogContent>
						<DialogActions>
							<Button onClick={handleCloseDialog}>Close</Button>
						</DialogActions>
					</>
				)}
			</Dialog>
		</Paper>
	);
};

export default TeacherOnboardingVideos;
